<script setup lang="ts">
/**
 * "Add to calendar" for an offer that has a real date attached - a reserved prize with a start time, or a hotel
 * arrival. Renders nothing for an offer that is only a window of validity, since that is not an appointment.
 *
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add App\components\offers\OfferCalendarLinks.vue.
 *
 * 2026-09-09 (0.2.0) (jhoward)
 *   - Replace the two action links with one 32px pill that opens a two-item panel. As bare anchors in
 *     .layout--actions they were floated, uppercase and capped at 50% of the row, so at the offers grid's 360px floor
 *     they wrapped the actions block to three rows, and at any width they read as peers of REDEEM and CANCEL.
 *     Both call sites mount the pill outside .layout--actions, which floats and uppercases anchors and nothing else -
 *     a pill left in there would sit out of flow ahead of the CTAs, with 'event' rendering as five capital letters.
 *   - Both destinations are kept, behind the one control: Google Calendar first as the one-tap path, then the .ics
 *     download for Apple Calendar and Outlook. Opens as a disclosure rather than a role="menu", which for two
 *     ordinary links would only add the obligation to implement arrow keys and a roving tabindex.
 *   - Single root element now, rather than the multi-root fragment, which could not accept a class from its parent.
 */

import { computed, nextTick, onBeforeUnmount, ref, useId } from 'vue';
import type { PropType } from 'vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getOfferCalendarEvent, { getGoogleCalendarUrl, getIcsForOffer } from '@/helpers/getOfferCalendarEvent';
import useAppStore from '@/store';

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
});

const appStore = useAppStore();
const event = computed(() => getOfferCalendarEvent(props.offer));
//Non-null whenever event is, since getGoogleCalendarUrl only returns null for an offer with no date at all - which is
//an offer this component does not render. The v-if on the item is there for the day that stops being true.
const googleCalendarUrl = computed(() => getGoogleCalendarUrl(props.offer));

const panelId = useId();
const isPanelOpen = ref(false);
//Set when the panel would run off the bottom of the window. .body--container is overflow: hidden, so a panel that
//overflows the page is clipped rather than scrolled to.
const isPanelFlipped = ref(false);
const container = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLElement | null>(null);

function handleOutsidePointerDown(pointerEvent: PointerEvent): void {
  if (!container.value?.contains(pointerEvent.target as Node)) {
    closePanel();
  }
}

function closePanel(shouldRestoreFocus: boolean = false): void {
  if (!isPanelOpen.value) {
    return;
  }

  isPanelOpen.value = false;
  isPanelFlipped.value = false;
  document.removeEventListener('pointerdown', handleOutsidePointerDown);

  //Only when the panel was dismissed from the keyboard; stealing focus back after a click would be a surprise.
  if (shouldRestoreFocus) {
    trigger.value?.focus();
  }
}

async function openPanel(): Promise<void> {
  isPanelOpen.value = true;
  document.addEventListener('pointerdown', handleOutsidePointerDown);

  //Measured after the panel exists, so the decision uses its real height rather than a guess.
  await nextTick();
  const triggerBounds = trigger.value?.getBoundingClientRect();
  const panelHeight = panel.value?.offsetHeight ?? 0;
  if (triggerBounds) {
    isPanelFlipped.value = triggerBounds.bottom + panelHeight + 8 > window.innerHeight;
  }
}

function handleTriggerClick(): void {
  if (isPanelOpen.value) {
    closePanel();
    return;
  }

  openPanel();
}

function handleEscape(keyboardEvent: KeyboardEvent): void {
  if (!isPanelOpen.value) {
    return;
  }

  //Swallowed only when it actually closed the panel, so an Escape with nothing open still reaches whatever is above.
  keyboardEvent.stopPropagation();
  closePanel(true);
}

function downloadIcs(): void {
  const ics = getIcsForOffer(props.offer);
  if (!ics) {
    appStore.setNewSnack(10000, 'This offer has no date to add to a calendar.', 'error');
    closePanel();
    return;
  }

  //text/calendar rather than a data: URI so iOS opens it in Calendar instead of rendering it as text.
  const blobUrl = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = (props.offer.OFFER_COPY_TITLE || 'offer').replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  //Revoked on the next frame; revoking synchronously can cancel the download in some browsers.
  requestAnimationFrame(() => URL.revokeObjectURL(blobUrl));

  closePanel();
}

//The offers grid animates with <transition-group>, so an instance can unmount while its panel is still open. A
//document listener left behind would hold a dead component and keep firing.
onBeforeUnmount(() => document.removeEventListener('pointerdown', handleOutsidePointerDown));
</script>

<template>
  <div v-if="event" ref="container" class="offer_calendar--container" @keydown.esc="handleEscape">
    <button
      ref="trigger"
      type="button"
      class="offer_calendar--trigger"
      :aria-expanded="isPanelOpen"
      :aria-controls="panelId"
      @click.stop.prevent="handleTriggerClick"
    >
      <span class="offer_calendar--trigger_icon material-icons" aria-hidden="true">event</span>
      <span class="offer_calendar--trigger_label">Add to calendar</span>
    </button>

    <!--A disclosure rather than a role="menu": a menu role would oblige arrow-key navigation and a roving tabindex
        for what is two ordinary links, and the native tab order already handles them.-->
    <div
      v-if="isPanelOpen"
      :id="panelId"
      ref="panel"
      class="offer_calendar--panel"
      :class="{ 'offer_calendar--panel-flipped': isPanelFlipped }"
    >
      <a
        v-if="googleCalendarUrl"
        class="offer_calendar--item"
        :href="googleCalendarUrl"
        target="_blank"
        rel="noopener noreferrer"
        @click="closePanel()"
      >
        <span class="offer_calendar--item_icon material-icons" aria-hidden="true">open_in_new</span>
        <span class="offer_calendar--item_text">
          <span class="offer_calendar--item_label">Google Calendar</span>
          <span class="offer_calendar--item_hint">Opens in a new tab</span>
        </span>
      </a>

      <button type="button" class="offer_calendar--item" @click.stop.prevent="downloadIcs">
        <span class="offer_calendar--item_icon material-icons" aria-hidden="true">download</span>
        <span class="offer_calendar--item_text">
          <span class="offer_calendar--item_label">Download .ics file</span>
          <span class="offer_calendar--item_hint">Apple Calendar, Outlook and others</span>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/*The pill below is <peppermill-chip>'s Material metrics written out: a 32px pill with a 16px corner, 12px of body
  padding, a 14px label with Material's chip tracking and an 18px leading icon inset 8px. px rather than em, the same
  way the chip and <material-button> are specified, so the shape does not move with whatever font-size it lands in.

  Not <peppermill-chip> itself because its only activation event is update:selected, which would put a permanent
  aria-pressed="false" on a control that is not a toggle, and because its aria-* would land on the wrapper span
  rather than the button that opens the panel. Collapse this block to a <peppermill-chip> if the framework ever
  gives it a one-shot action mode.*/
.offer_calendar--container {
  position: relative;
  display: inline-flex;
}

.offer_calendar--trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(var(--primary-color), 0.38);
  border-radius: 16px;
  background-color: transparent;
  color: rgb(var(--primary-color));
  /*A button inherits no font family, and the shorthand has to come first or it resets the two below it.*/
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.0178571em;
  white-space: nowrap;
  vertical-align: middle;
  cursor: pointer;
  transition:
    background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

/*32px clears the 24px WCAG 2.5.8 minimum but not the 44px pointer target Material asks for, so the target is padded
  past the pill without moving anything in the row. Both of its neighbours in the status row are non-interactive, so
  the overlap cannot steal a click.*/
.offer_calendar--trigger::after {
  content: '';
  position: absolute;
  top: -6px;
  right: -4px;
  bottom: -6px;
  left: -4px;
}

.offer_calendar--trigger:hover,
.offer_calendar--trigger[aria-expanded='true'] {
  border-color: rgba(var(--primary-color), 0.6);
  background-color: rgba(var(--primary-color), 0.1);
}

.offer_calendar--trigger:active {
  background-color: rgba(var(--primary-color), 0.24);
}

.offer_calendar--trigger:focus-visible {
  outline: 2px solid rgb(var(--primary-color));
  outline-offset: 2px;
}

/*text-transform is inherited, and an uppercased ligature is not a ligature - 'event' would render as five letters.
  Reset here rather than trusting wherever this control is mounted.*/
.offer_calendar--trigger_icon {
  flex: 0 0 auto;
  margin-left: -4px;
  margin-right: 4px;
  font-size: 18px;
  line-height: 1;
  text-transform: none;
}

/*A flex item defaults to min-width: auto, so without this the ellipsis never applies.*/
.offer_calendar--trigger_label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/*Above .offer--linked_account, which already holds z-index: 1 in this stacking context.*/
.offer_calendar--panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 2;
  min-width: 15em;
  padding: 4px 0;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 2px 2px 8px 0 rgba(0, 0, 0, 0.35);
}

.offer_calendar--panel-flipped {
  top: auto;
  bottom: calc(100% + 4px);
}

.offer_calendar--item {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background-color: transparent;
  color: rgba(0, 0, 0, 0.87);
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  text-decoration: none;
  text-transform: none;
  cursor: pointer;
}

.offer_calendar--item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.offer_calendar--item:focus-visible {
  outline: 2px solid rgb(var(--primary-color));
  outline-offset: -2px;
}

.offer_calendar--item_icon {
  flex: 0 0 auto;
  margin-right: 12px;
  color: rgb(var(--primary-color));
  font-size: 18px;
  line-height: 20px;
  text-transform: none;
}

.offer_calendar--item_text {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.offer_calendar--item_hint {
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.6);
}

@media (prefers-reduced-motion: reduce) {
  .offer_calendar--trigger {
    transition: none;
  }
}

/*Nothing to press on paper.*/
@media print {
  .offer_calendar--container {
    display: none;
  }
}
</style>
